import type { ObjectDefinition } from '../../../types/Schema';
import type { FieldPath } from '../../../types/Field';
import type { InitiatorType, InvalidLinkedPaths } from '../../../reducers/studio/fields/types';
import type { RESET_STUDIO_ACTION } from '../../../reducers/consts';
import type { ContainerAPIMethodName, ContainerAPIMethodResultForMethod } from '../../../modules/container-api-methods';
import type { StudioAsset } from '../../../reducers/studio/assets';
import type { RemoteAsset, FieldModel } from '../../../types/FieldModel';
import type AppError from '../../../lib/AppError';

export enum ValidationType {
    All = 'all',
    Fields = 'fields'
}

export type FieldError = {
    fieldPath: FieldPath;
    srcType: string;
    srcObjectId: string;
    srcProjectId: string;
};

export type FieldErrorData = {
    message: string;
    data: {
        isUniqueValidation?: boolean;
        field: FieldError;
    };
};

export type FieldsToValidate = {
    srcType: string;
    srcObjectId: string;
    srcProjectId: string;
    fieldPath: FieldPath;
};

export type InvalidField = {
    isValid: false;
    srcType: string;
    srcObjectId: string;
    srcProjectId: string;
    fieldPath: FieldPath;
    revalidationNeeded: boolean;
    message: string;
};

export type ValidField = {
    isValid: true;
    srcType: string;
    srcObjectId: string;
    srcProjectId: string;
    revalidationNeeded: boolean;
    fieldPath: FieldPath;
};

export type FieldValidationError = { message?: string; isValidating?: boolean; revalidationNeeded?: boolean; type?: FieldErrorType };

export enum FieldErrorType {
    field = 'field',
    childField = 'childField'
}

export type ValidatedField = InvalidField | ValidField;

export type StudioFieldsValidationActions =
    | { type: typeof RESET_STUDIO_ACTION }
    | {
          type: 'VALIDATE_FIELDS_FAILED';
      }
    | {
          type: 'VALIDATE_FIELDS_STARTED';
          data: {
              resetValidationData?: boolean;
              fields: ValidField[] | null;
          };
      }
    | {
          type: 'VALIDATE_FIELDS_SUCCEEDED';
          data: ValidatedField[] | null;
      }
    | {
          type: 'SET_REVALIDATION_OBJECTS';
          data: ValidatedField[] | null;
      }
    | {
          type: 'SET_VALIDATE_FIELDS_INITIATOR';
          data: {
              initiator: {
                  type: InitiatorType;
                  srcObjectId: string;
              } | null;
          };
      }
    | {
          type: 'SET_LINKED_INVALID_PATHS';
          data: {
              linkedPaths: InvalidLinkedPaths;
          };
      };

export const CHANGES_STATE = {
    CHANGED: 'CHANGED',
    REQUEST_SENT: 'REQUEST_SENT',
    REQUEST_SUCCESS: 'REQUEST_SUCCESS',
    REQUEST_FAILED: 'REQUEST_FAILED',
    CHANGE_APPLIED: 'CHANGE_APPLIED',
    CHANGE_LOCAL_APPLIED: 'CHANGE_LOCAL_APPLIED',
    INVALID: 'INVALID'
} as const;

export type CHANGES_STATE_ARR = Array<keyof typeof CHANGES_STATE>;

export type ChangedFieldAction = 'value' | 'setObject' | 'remove' | 'order' | 'create' | 'link' | 'add' | 'linkAsset' | 'uploadAsset';

export interface ChangedField extends ObjectDefinition {
    id: string;
    fieldPath: FieldPath;
    objectFieldPath: FieldPath;
    relativeFieldPath: FieldPath;
    action: ChangedFieldAction;
    state: keyof typeof CHANGES_STATE;
    error?: AppError;
    sentDate?: Date;
    changes: {
        locale?: string;
        linkedObjectId?: string;
        fieldDataPath: FieldPath;
        fieldPath: FieldPath;
        srcEnvironment: string | null;
        srcObjectId: string;
        srcProjectId: string;
        srcType: string;
        modelField: FieldModel;

        value?: string | string[];
        setObject?: {
            selectedSrcType: string;
            selectedProjectId: string;
            selectedModelName?: string;
            selectedObjectId?: string;
            values?: Record<string, $TSFixMe>;
        };
        remove?: {
            key?: string;
            index?: number;
        };
        order?: number[];
        add?: {
            selectedProjectId: string;
            selectedSrcType: string;
            selectedModelName?: string;
            selectedObjectId?: string;
            values?: Record<string, $TSFixMe>;
            position?: number;
        };

        // todo: add support for link asset & upload asset cross reference (in the editor.ts)
        linkAsset?: {
            id: string;
            asset?: StudioAsset | RemoteAsset;
            selectedProjectId: string;
            selectedSrcType: string;
        };
        uploadAsset?: {
            uri: string;
            source: string;
            metadata: $TSFixMe;
            selectedProjectId: string;
            selectedSrcType: string;
        };
    };
    onChangeApplied?: Array<(changedField: ChangedField) => void>;
    result?: ContainerAPIMethodResultForMethod<ContainerAPIMethodName>;
}
